import { Reference } from '@approvalmax/types';
import { useAtom } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { useCallback, useEffect } from 'react';

import {
    calculateLineAmountsBasedOnAmount,
    reorderLines,
    setCurrencyPrecision,
    setExpenseLineCurrencyPrecision,
} from '../../helpers';
import { checkExpenseLineIsEmpty } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteExpenseItemsState } from '../../resources/states';
import { useChangeNetSuiteRequest } from '../useChangeNetSuiteRequest/useChangeNetSuiteRequest';
import { useCurrencyPrecision } from '../useCurrencyPrecision/useCurrencyPrecision';
import { useGetAndIncreaseItemLineNumber } from '../useGetAndIncreaseItemLineNumber/useGetAndIncreaseItemLineNumber';
import { useUpdateLineCustomField } from '../useUpdateLineCustomField/useUpdateLineCustomField';
import {
    createNewExpenseLine,
    expandExpenseLine,
    generateExpenseLineId,
    getUpdatedExpenseLine,
} from './useExpenseItems.helpers';
import {
    CloneLineHandler,
    ExpenseItemsUpdateAccountHandler,
    ExpenseItemsUpdateStateHandler,
    ExpenseItemUpdateHandler,
    ExpenseItemUpdateOnCurrencyChangeHandler,
    FieldUpdateHandler,
    MultiLineFieldUpdateHandler,
    RemoveLineHandler,
    ReorderHandler,
} from './useExpenseItems.types';

export const useExpenseItems = (currency: Reference | null | undefined) => {
    const [expenseItems, setExpenseItemsState] = useAtom(netSuiteExpenseItemsState);

    const { currencies } = useSelector(getNetSuiteContext);

    const currencyPrecision = useCurrencyPrecision(currency);

    const getAndIncreaseItemLineNumber = useGetAndIncreaseItemLineNumber();

    useEffect(() => {
        if (!expenseItems.length) {
            const itemLineNumber = getAndIncreaseItemLineNumber();

            setExpenseItemsState([createNewExpenseLine(itemLineNumber)]);
        }
    }, [expenseItems, setExpenseItemsState, getAndIncreaseItemLineNumber]);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const setExpenseItems = useCallback(
        (data: domain.NetSuiteExpenseLine[]) => {
            if (data.length === 0) {
                const itemLineNumber = getAndIncreaseItemLineNumber();

                setExpenseItemsState([createNewExpenseLine(itemLineNumber)]);

                return;
            }

            setExpenseItemsState(
                data.map((expenseItem) =>
                    expandExpenseLine({
                        ...expenseItem,
                        ...calculateLineAmountsBasedOnAmount(
                            expenseItem.amount,
                            expenseItem.taxRate,
                            currencyPrecision
                        ),
                        amortizationResidual: setCurrencyPrecision(expenseItem.amortizationResidual, currencyPrecision),
                    })
                )
            );
        },
        [currencyPrecision, getAndIncreaseItemLineNumber, setExpenseItemsState]
    );

    const onAddLine = useCallback(() => {
        const itemLineNumber = getAndIncreaseItemLineNumber();

        setExpenseItemsState((current) => [...current, createNewExpenseLine(itemLineNumber)]);

        changeNetSuiteRequest();
    }, [changeNetSuiteRequest, getAndIncreaseItemLineNumber, setExpenseItemsState]);

    const onReorder = useCallback<ReorderHandler>(
        (oldIndex, newIndex) => {
            setExpenseItemsState((current) => reorderLines(current, oldIndex, newIndex));

            changeNetSuiteRequest();
        },
        [setExpenseItemsState, changeNetSuiteRequest]
    );

    const onRemoveLine = useCallback<RemoveLineHandler>(
        (id) => {
            setExpenseItemsState((current) => current.filter((item) => item.id !== id));

            changeNetSuiteRequest();
        },
        [setExpenseItemsState, changeNetSuiteRequest]
    );

    const onCloneLine = useCallback<CloneLineHandler>(
        (id) => {
            const itemLineNumber = getAndIncreaseItemLineNumber();

            setExpenseItemsState((current) => {
                const targetItem = current.find((item) => item.id === id);

                if (targetItem) {
                    return [
                        ...current,
                        {
                            ...targetItem,
                            id: generateExpenseLineId(),
                            lineNumber: itemLineNumber,
                        },
                    ];
                }

                return current;
            });

            changeNetSuiteRequest();
        },
        [getAndIncreaseItemLineNumber, setExpenseItemsState, changeNetSuiteRequest]
    );

    const onFieldUpdate = useCallback<FieldUpdateHandler>(
        (id, key, value) => {
            setExpenseItemsState((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        return getUpdatedExpenseLine(item, key, value, currencyPrecision);
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setExpenseItemsState, changeNetSuiteRequest]
    );

    const onMultiLineFieldUpdate = useCallback<MultiLineFieldUpdateHandler>(
        (key, value) => {
            setExpenseItemsState((current) =>
                current.map((item) => getUpdatedExpenseLine(item, key, value, currencyPrecision))
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setExpenseItemsState, changeNetSuiteRequest]
    );

    const updateExpenseItem = useCallback<ExpenseItemUpdateHandler>(
        (id, data) => {
            setExpenseItemsState((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        const updatedItem = { ...item, ...data };

                        updatedItem.isEmpty = checkExpenseLineIsEmpty(updatedItem);

                        return updatedItem;
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [setExpenseItemsState, changeNetSuiteRequest]
    );

    const updateExpenseItemsOnCurrencyChange = useCallback<ExpenseItemUpdateOnCurrencyChangeHandler>(
        (prevCurrency, newCurrency) => {
            setExpenseItemsState((current) => {
                return current.map((item) => {
                    if (!newCurrency) {
                        return item;
                    }

                    let newItem = item;

                    const newCurrencyPrecision = currencies.find(({ id }) => id === newCurrency?.id)?.precision ?? 2;

                    if (currencyPrecision !== newCurrencyPrecision) {
                        newItem = setExpenseLineCurrencyPrecision(newItem, newCurrencyPrecision);
                    }

                    if (newItem.rates) {
                        const prevCategoryRate = newItem.rates.find((rate) => {
                            return rate.currency === prevCurrency?.text || rate.currency === prevCurrency?.id;
                        });

                        const newCategoryRate = newItem.rates.find((rate) => {
                            return rate.currency === newCurrency?.text || rate.currency === newCurrency?.id;
                        });

                        if (!newCategoryRate) {
                            return newItem;
                        }

                        if (
                            (typeof newItem.amount === 'number' && newItem.amount === prevCategoryRate?.defaultRate) ||
                            (typeof newItem.amount !== 'number' && !prevCategoryRate)
                        ) {
                            return {
                                ...newItem,
                                amount: newCategoryRate.defaultRate,
                            };
                        }
                    }

                    return newItem;
                });
            });

            changeNetSuiteRequest();
        },
        [currencies, currencyPrecision, setExpenseItemsState, changeNetSuiteRequest]
    );

    const updateCustomField = useUpdateLineCustomField(setExpenseItemsState);

    const updateExpenseItemsState = useCallback<ExpenseItemsUpdateStateHandler>(
        (updateFn) => {
            setExpenseItemsState(updateFn);
        },
        [setExpenseItemsState]
    );

    const updateExpenseItemsAccount = useCallback<ExpenseItemsUpdateAccountHandler>(
        (defaultExpenseAccount: Reference | null) => {
            setExpenseItemsState((current) => {
                return current.map((line) => {
                    const newLine = {
                        ...line,
                        account: line.category ? line.account : defaultExpenseAccount || line.account,
                    };

                    newLine.isEmpty = checkExpenseLineIsEmpty(newLine);

                    return newLine;
                });
            });
        },
        [setExpenseItemsState]
    );

    return {
        expenseItems,
        setExpenseItems,
        onAddLine,
        onCloneLine,
        onRemoveLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        onReorder,
        updateExpenseItem,
        updateExpenseItemsOnCurrencyChange,
        updateCustomField,
        updateExpenseItemsState,
        updateExpenseItemsAccount,
    };
};
